import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar, ExternalLink, FileText, ChevronDown } from 'lucide-react';
import { format, isPast } from 'date-fns';

interface SpaProgram {
  id: string;
  title: string;
  description: string;
  spa_pillar: string | null;
  target_audience: string[];
  deadline: string | null;
  link_url: string | null;
  country: string | null;
  created_at: string;
}

const PILLARS = ['critical_minerals', 'industrialization', 'infrastructure', 'governance'] as const;
const AUDIENCES = ['Diaspora', 'SME', 'Investor', 'Youth', 'Academic', 'Government', 'Other'] as const;

const PILLAR_BORDER: Record<string, string> = {
  critical_minerals: 'border-l-[hsl(var(--spa-minerals))]',
  industrialization: 'border-l-[hsl(var(--spa-industrialization))]',
  infrastructure: 'border-l-[hsl(var(--spa-infrastructure))]',
  governance: 'border-l-[hsl(var(--spa-governance))]',
};

const PILLAR_BADGE: Record<string, string> = {
  critical_minerals: 'bg-spa-minerals/10 text-spa-minerals border-spa-minerals/30',
  industrialization: 'bg-spa-industrialization/10 text-spa-industrialization border-spa-industrialization/30',
  infrastructure: 'bg-spa-infrastructure/10 text-spa-infrastructure border-spa-infrastructure/30',
  governance: 'bg-spa-governance/10 text-spa-governance border-spa-governance/30',
};

export default function SpaPrograms() {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState<SpaProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [pillarFilter, setPillarFilter] = useState<string | null>(null);
  const [audienceFilters, setAudienceFilters] = useState<string[]>([]);

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const { data, error } = await supabase
        .from('spa_programs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPrograms(
        (data || []).map((p: any) => ({
          ...p,
          target_audience: Array.isArray(p.target_audience) ? p.target_audience : [],
        }))
      );
    } catch (error) {
      console.error('Error fetching programs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAudience = (a: string) => {
    setAudienceFilters(prev => (prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]));
  };

  const filtered = programs.filter(p => {
    if (pillarFilter && p.spa_pillar !== pillarFilter) return false;
    if (audienceFilters.length > 0 && !audienceFilters.some(a => p.target_audience.includes(a))) return false;
    return true;
  });

  return (
    <Layout>
      <div className="container py-8">
        <h1 className="text-3xl font-bold">{t('programs.title')}</h1>
        <p className="mt-2 mb-8 text-muted-foreground">{t('programs.description')}</p>

        {/* Filter bar */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          {/* Pillar tabs */}
          <div className="flex flex-wrap gap-1">
            <Button
              variant={pillarFilter === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPillarFilter(null)}
            >
              {t('common.reset') === 'Reset' ? 'All' : 'Tous'}
            </Button>
            {PILLARS.map(p => (
              <Button
                key={p}
                variant={pillarFilter === p ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPillarFilter(pillarFilter === p ? null : p)}
              >
                {t(`spaPillars.${p}`)}
              </Button>
            ))}
          </div>

          {/* Audience dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                {t('programs.filterAudience')}
                {audienceFilters.length > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">{audienceFilters.length}</Badge>
                )}
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {AUDIENCES.map(a => (
                <DropdownMenuCheckboxItem
                  key={a}
                  checked={audienceFilters.includes(a)}
                  onCheckedChange={() => toggleAudience(a)}
                >
                  {a}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Results */}
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="border-l-4">
                <CardContent className="p-6 space-y-3">
                  <Skeleton className="h-6 w-2/3" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/5" />
                  <div className="flex gap-2">
                    <Skeleton className="h-6 w-16 rounded-full" />
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-16 w-16 text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground">{t('programs.noResults')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(program => {
              const borderClass = program.spa_pillar ? PILLAR_BORDER[program.spa_pillar] || '' : 'border-l-muted';
              const badgeClass = program.spa_pillar ? PILLAR_BADGE[program.spa_pillar] : null;
              const deadlinePast = program.deadline ? isPast(new Date(program.deadline)) : false;

              return (
                <Card key={program.id} className={`border-l-4 ${borderClass}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1 space-y-2">
                        <h3 className="text-lg font-bold">{program.title}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-3">{program.description}</p>

                        {/* Audience tags */}
                        {program.target_audience.length > 0 && (
                          <div className="flex flex-wrap gap-1.5">
                            {program.target_audience.map((a: string) => (
                              <Badge key={a} variant="secondary" className="text-xs font-normal">{a}</Badge>
                            ))}
                          </div>
                        )}

                        {/* Deadline */}
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          {program.deadline ? (
                            deadlinePast ? (
                              <span className="text-destructive font-medium">
                                {t('programs.closed')} — {format(new Date(program.deadline), 'MMM d, yyyy')}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">
                                {t('programs.deadline')}: {format(new Date(program.deadline), 'MMM d, yyyy')}
                              </span>
                            )
                          ) : (
                            <span className="text-muted-foreground">{t('programs.noDeadline')}</span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3">
                        {/* Pillar badge */}
                        {program.spa_pillar && badgeClass && (
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badgeClass}`}>
                            {t(`spaPillars.${program.spa_pillar}`)}
                          </span>
                        )}

                        {/* CTA */}
                        {program.link_url ? (
                          <Button asChild size="sm" className="gap-2">
                            <a href={program.link_url} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="h-3.5 w-3.5" />
                              {t('programs.learnMore')}
                            </a>
                          </Button>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button size="sm" disabled className="gap-2">
                                <ExternalLink className="h-3.5 w-3.5" />
                                {t('programs.learnMore')}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t('misc.noLinkAvailable')}</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
